import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Clock3, FileText, Search, Stethoscope, Users } from 'lucide-react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useApiClient } from '../lib/apiClient';

type SearchPayload = {
  patients: Array<{ id: string; name: string; mrn: string; dob: string }>;
  appointments: Array<{ id: string; title: string | null; startTime: string; patientName: string; patientId: string; status: string }>;
  providers: Array<{ id: string; name: string; role: string }>;
  encounters: Array<{ id: string; patientName: string; status: string; date: string }>;
  records: Array<{ id: string; type: string; title: string; patientName: string; patientId: string; updatedAt: string }>;
  keyData: Array<{ id: string; type: string; label: string; detail: string; patientId: string | null; updatedAt: string }>;
};

type Category = 'clients' | 'records' | 'appointments' | 'keyData';

const categoryMeta: Record<Category, { label: string; icon: typeof Users }> = {
  clients: { label: 'Clients', icon: Users },
  records: { label: 'Records', icon: FileText },
  appointments: { label: 'Appointments', icon: CalendarDays },
  keyData: { label: 'Key data', icon: Stethoscope },
};

export default function UniversalSearchPage() {
  const { fetchJson } = useApiClient();
  const [query, setQuery] = useState('');
  const [activeCategories, setActiveCategories] = useState<Category[]>(['clients', 'records', 'appointments', 'keyData']);
  const [data, setData] = useState<SearchPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setData(null);
      return;
    }
    const handle = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await fetchJson<SearchPayload>(`/search?q=${encodeURIComponent(q)}&limit=10`);
        setData(payload);
      } catch (err: any) {
        setError(err?.message || 'Search failed.');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [fetchJson, query]);

  const rows = useMemo(() => {
    if (!data) return [];
    return [
      ...data.patients.map((item) => ({
        id: `client:${item.id}`,
        category: 'clients' as Category,
        label: item.name,
        meta: `MRN ${item.mrn} - DOB ${new Date(item.dob).toLocaleDateString()}`,
        href: `/patients/${item.id}`,
        updatedAt: item.dob,
      })),
      ...data.appointments.map((item) => ({
        id: `appointment:${item.id}`,
        category: 'appointments' as Category,
        label: item.title || item.patientName,
        meta: `${item.status} - ${new Date(item.startTime).toLocaleString()}`,
        href: `/appointments?openId=${item.id}`,
        updatedAt: item.startTime,
      })),
      ...data.records.map((item) => ({
        id: `record:${item.id}`,
        category: 'records' as Category,
        label: item.title,
        meta: `${item.type} - ${item.patientName}`,
        href: item.patientId ? `/patients/${item.patientId}` : '/patients',
        updatedAt: item.updatedAt,
      })),
      ...data.keyData.map((item) => ({
        id: `key:${item.id}`,
        category: 'keyData' as Category,
        label: item.label,
        meta: `${item.type} - ${item.detail}`,
        href: item.patientId ? `/patients/${item.patientId}` : '/patients',
        updatedAt: item.updatedAt,
      })),
    ]
      .filter((item) => activeCategories.includes(item.category))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [activeCategories, data]);

  const totalsByCategory = useMemo(() => {
    const counts: Record<Category, number> = { clients: 0, records: 0, appointments: 0, keyData: 0 };
    rows.forEach((item) => {
      counts[item.category] += 1;
    });
    return counts;
  }, [rows]);

  const options: Highcharts.Options = {
    title: { text: undefined },
    chart: { type: 'column', height: 260, backgroundColor: 'transparent' },
    credits: { enabled: false },
    xAxis: { categories: ['Clients', 'Records', 'Appointments', 'Key data'] },
    yAxis: { title: { text: 'Matches' }, allowDecimals: false },
    legend: { enabled: false },
    series: [
      {
        type: 'column',
        data: [totalsByCategory.clients, totalsByCategory.records, totalsByCategory.appointments, totalsByCategory.keyData],
        color: '#0ea5e9',
      },
    ],
  };

  return (
    <div className="space-y-6">
      <header>
        <p className="text-sm uppercase tracking-wide text-slate-400">General</p>
        <h1 className="text-2xl font-semibold text-ink">Global Search</h1>
        <p className="text-sm text-slate-500">Search across clients, records, appointments, and key operational data.</p>
      </header>

      <section className="card space-y-3 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Try a client name, MRN, appointment, note, invoice, or form..."
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 pl-9 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(categoryMeta) as Category[]).map((category) => {
            const active = activeCategories.includes(category);
            const Icon = categoryMeta[category].icon;
            return (
              <button
                key={category}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                  active ? 'border-ink bg-ink text-white' : 'border-slate-200 bg-white text-slate-700'
                }`}
                onClick={() =>
                  setActiveCategories((prev) =>
                    prev.includes(category) ? prev.filter((entry) => entry !== category) : [...prev, category],
                  )
                }
              >
                <Icon size={14} />
                {categoryMeta[category].label}
              </button>
            );
          })}
        </div>
      </section>

      {error ? <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="card overflow-hidden p-0">
          <div className="grid grid-cols-12 border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-xs uppercase tracking-wide text-slate-500">
            <div className="col-span-4">Result</div>
            <div className="col-span-5">Details</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1 text-right">Open</div>
          </div>
          <div className="divide-y divide-slate-100">
            {loading ? <div className="p-4 text-sm text-slate-500">Searching...</div> : null}
            {!loading && query.trim() && !rows.length ? <div className="p-4 text-sm text-slate-500">No results match the current filters.</div> : null}
            {!query.trim() ? <div className="p-4 text-sm text-slate-500">Enter a search term to begin.</div> : null}
            {rows.map((item) => {
              const Icon = categoryMeta[item.category].icon;
              return (
                <a key={item.id} href={item.href} className="grid grid-cols-12 items-center px-4 py-3 text-sm hover:bg-slate-50">
                  <div className="col-span-4 font-semibold text-ink">{item.label}</div>
                  <div className="col-span-5 text-slate-600">{item.meta}</div>
                  <div className="col-span-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-1 text-xs text-sky-700">
                      <Icon size={12} />
                      {categoryMeta[item.category].label}
                    </span>
                  </div>
                  <div className="col-span-1 text-right text-xs text-slate-400">
                    <Clock3 size={12} className="ml-auto" />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-ink">Results Mix</h2>
          <p className="mt-1 text-xs text-slate-500">Matches by domain for the current query scope.</p>
          <div className="mt-3">
            <HighchartsReact highcharts={Highcharts} options={options} />
          </div>
        </div>
      </section>
    </div>
  );
}
